import { formatarBRL } from '../../lib/formato'

/**
 * O que a página faz com o erro que voltou do envio do pedido, quando ele tem a ver com cashback
 * (spec `docs/specs/cashback-uso-no-cardapio.md` §4.1 e §7.5). Só funções puras: a página executa
 * o que sai daqui. O erro é lido pelo `codigo`, nunca pelo texto.
 */

export const reacoesAoErroDoPedido = [
  'OFERECER_REENVIO',
  'ATUALIZAR_SALDO',
  'TROCAR_CHAVE_E_REENVIAR',
  'DESLIGAR_CASHBACK',
  'MOSTRAR_MENSAGEM',
] as const

/**
 * - OFERECER_REENVIO: guarda o corpo enviado e mostra o "Tentar de novo", que reenvia esse mesmo corpo;
 * - ATUALIZAR_SALDO: refaz a consulta do saldo e conta ao cliente quanto dá para usar agora;
 * - TROCAR_CHAVE_E_REENVIAR: descarta a chave de envio e reenvia o mesmo corpo, uma vez só;
 * - DESLIGAR_CASHBACK: desliga o switch, refaz a consulta do saldo e mostra a mensagem do backend;
 * - MOSTRAR_MENSAGEM: mostra a mensagem do backend, como a página sempre fez com qualquer erro.
 */
export type ReacaoAoErroDoPedido = (typeof reacoesAoErroDoPedido)[number]

/** `codigo` é o do corpo do erro. Vem null quando o erro não tem corpo (a rede caiu, por exemplo). */
export function reacaoAoErroDoPedido(codigo: string | null): ReacaoAoErroDoPedido {
  // O débito pode ter sido feito sem a resposta chegar. Só o MESMO pedido, com a mesma chave, reaproveita esse débito.
  if (codigo === 'CASHBACK_INDISPONIVEL') return 'OFERECER_REENVIO'

  // O saldo ou o limite da loja mudou depois que o cliente viu o valor.
  if (codigo === 'CASHBACK_ALTERADO') return 'ATUALIZAR_SALDO'

  // A chave de envio não serve mais para debitar.
  if (codigo === 'CASHBACK_CHAVE_ENCERRADA') return 'TROCAR_CHAVE_E_REENVIAR'

  // O cliente não tem conta no cashback, ou a loja parou de aceitar: o pedido segue sem o cashback.
  if (codigo === 'CASHBACK_SEM_CONTA') return 'DESLIGAR_CASHBACK'
  if (codigo === 'CASHBACK_USO_DESLIGADO') return 'DESLIGAR_CASHBACK'

  // CASHBACK_EXIGE_LOGIN, VALOR_A_PAGAR_INSUFICIENTE, TOTAL_DIVERGENTE e DESCONTO_DIVERGENTE caem aqui, com os erros que não são de cashback.
  return 'MOSTRAR_MENSAGEM'
}

/**
 * O envio falhou sem deixar claro se o pedido chegou ao backend: não houve resposta (rede, tempo esgotado,
 * corpo ilegível) ou ela foi um 5xx. O pedido e o débito do cashback podem ter acontecido. Um 4xx é o
 * backend recusando: aí o pedido não foi gravado.
 */
export function envioPodeTerChegado(erro: unknown): boolean {
  if (typeof erro !== 'object' || erro === null) return true
  const { status, originalStatus } = erro as { status?: unknown; originalStatus?: unknown }
  if (typeof status === 'number') return status >= 500

  // Resposta que não deu para ler (PARSING_ERROR, ex.: página HTML de um proxy) vale pelo status que veio com ela.
  if (typeof originalStatus === 'number') return originalStatus >= 500

  // FETCH_ERROR, TIMEOUT_ERROR ou erro sem formato conhecido: nenhuma resposta HTTP chegou.
  return true
}

/** O que o cliente lê depois de CASHBACK_ALTERADO. `maximoAgora` é quanto dá para usar com o saldo novo, em reais. */
export function mensagemDeCashbackAlterado(maximoAgora: number): string {
  if (maximoAgora <= 0) {
    return 'Seu saldo de cashback mudou e não dá mais para usar neste pedido. Confira o total e envie o pedido de novo.'
  }
  return `Seu saldo de cashback mudou. Agora dá para usar ${formatarBRL(maximoAgora)}. Confira o total e envie o pedido de novo.`
}

/** O cupom digitado foi aplicado na hora de enviar e mudou quanto de cashback dá para usar: o cliente confere antes. */
export const TEXTO_CONFIRA_DEPOIS_DO_CUPOM = 'Aplicamos seu cupom e os valores mudaram. Confira o total e envie o pedido de novo.'
